import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { API, formatApiError } from "@/lib/content";
import { ServiceChips, Field, inputCls } from "./ServiceChips";
import { PromoEstimate } from "./PromoEstimate";

const EMPTY = { name: "", email: "", phone: "", property_type: "House", size: "", frequency: "One-time", notes: "" };
const TYPES = ["House", "Apartment", "Office", "Rental / Airbnb", "Other"];
const FREQ = ["One-time", "Weekly", "Bi-weekly", "Monthly"];

const Pills = ({ options, value, onChange, testid }) => (
  <div className="flex flex-wrap gap-2" data-testid={testid}>
    {options.map((o) => (
      <button type="button" key={o} onClick={() => onChange(o)} data-testid={`${testid}-${o.toLowerCase().replace(/[^a-z]+/g, "-")}`}
        className={`rounded-full border px-4 py-2 text-sm transition-colors ${value === o ? "border-white bg-white font-semibold text-[#0B0F17]" : "border-white/15 text-slate-200 hover:border-white/50"}`}>
        {o}
      </button>
    ))}
  </div>
);

export const QuoteForm = ({ services, setServices, promo, setPromo }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!services.length) return toast.error("Choose at least one service");
    setBusy(true);
    try {
      await axios.post(`${API}/quotes`, { ...f, services, promo_code: promo?.code ?? "" });
      setDone(true);
      toast.success("Quote request sent");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (done)
    return (
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="py-10 text-center" data-testid="quote-success">
        <CheckCircle2 className="mx-auto h-14 w-14 text-amber-300" />
        <h3 className="mt-5 font-display text-3xl font-bold text-white">Quote request received.</h3>
        <p className="mx-auto mt-3 max-w-md text-slate-300">We'll review your details and send a free, no-obligation quote.</p>
        <button onClick={() => { setDone(false); setF(EMPTY); }} data-testid="quote-new-btn" className="mt-8 rounded-full border border-white/20 px-6 py-3 text-sm font-semibold text-white hover:border-amber-300">Send another</button>
      </motion.div>
    );

  return (
    <form onSubmit={submit} className="space-y-8" data-testid="quote-form">
      <Field label="Services"><ServiceChips value={services} onChange={setServices} prefix="quote" /></Field>
      <div className="grid gap-8 lg:grid-cols-2">
        <Field label="Property type"><Pills options={TYPES} value={f.property_type} onChange={(v) => setF({ ...f, property_type: v })} testid="quote-type" /></Field>
        <Field label="How often"><Pills options={FREQ} value={f.frequency} onChange={(v) => setF({ ...f, frequency: v })} testid="quote-frequency" /></Field>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Size (bedrooms / sq ft)"><input value={f.size} onChange={set("size")} placeholder="e.g. 3 bed, 2 bath" className={inputCls} data-testid="quote-size-input" /></Field>
        <Field label="Full name"><input required value={f.name} onChange={set("name")} className={inputCls} data-testid="quote-name-input" /></Field>
        <Field label="Email"><input required type="email" value={f.email} onChange={set("email")} className={inputCls} data-testid="quote-email-input" /></Field>
        <Field label="Phone"><input required value={f.phone} onChange={set("phone")} className={inputCls} data-testid="quote-phone-input" /></Field>
      </div>
      <Field label="Tell us more (optional)"><textarea rows={3} value={f.notes} onChange={set("notes")} className={`${inputCls} h-auto py-3`} data-testid="quote-notes-input" /></Field>
      <PromoEstimate services={services} promo={promo} setPromo={setPromo} prefix="quote" />
      <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }} disabled={busy} type="submit" data-testid="quote-submit-btn" className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-white px-8 py-4 font-semibold text-[#0B0F17] transition-colors hover:bg-amber-300 disabled:opacity-60 sm:w-auto">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Get my free quote
      </motion.button>
    </form>
  );
};
